import clsx from 'clsx';

export interface SnapshotEntry {
  label: string;
  /** Pre-formatted value — `'—'` placeholder permitted for missing data. */
  value: string;
}

export interface SnapshotBoxProps {
  entries: SnapshotEntry[];
  /**
   * When true, drop outer border / radius / background — caller provides
   * its own framing (e.g. the 170px Property-Visual-Grid slabs which share
   * a single outer border per cell).
   */
  bare?: boolean;
  className?: string;
}

/**
 * Vertically-stacked label/value pair list — used as the middle pane of
 * the Property Visual Context row. Operating-asset variant supplies 5
 * entries (Property Type · Location · Units / Mix · NOI · Acquisition
 * Date); New-development variant swaps to its own 5 fields.
 *
 * Empty `entries` array → renders an "—" caption row so the layout doesn't
 * collapse. Caller is responsible for the operating-asset vs new-development
 * field selection (configuration over conditionals — variant components
 * pass distinct entry lists rather than this primitive branching internally).
 */
export function SnapshotBox({ entries, bare = false, className }: SnapshotBoxProps) {
  return (
    <dl
      data-testid="snapshot-box"
      data-entry-count={entries.length}
      className={clsx(
        'flex flex-col gap-[5px] overflow-hidden',
        bare ? 'px-2.5 py-2 h-full' : 'gap-2 px-4 py-3 border border-panel-border rounded-[3px] bg-panel-bg',
        className,
      )}
    >
      {entries.length === 0 ? (
        <div className="text-micro font-mono text-text-dim">—</div>
      ) : (
        entries.map((entry) => (
          <div
            key={entry.label}
            data-snapshot-label={entry.label}
            className="flex items-baseline justify-between gap-3 min-w-0"
          >
            <dt className="text-[7px] font-mono text-text-muted uppercase tracking-[1.1px] font-semibold shrink-0">
              {entry.label}
            </dt>
            <dd className="text-[9.5px] font-mono leading-[1.25] tabular-nums text-text-bright text-right truncate font-medium">
              {entry.value}
            </dd>
          </div>
        ))
      )}
    </dl>
  );
}
